/** biome-ignore-all lint/correctness/useUniqueElementIds: unique login component */
/** biome-ignore-all lint/a11y/useValidAnchor: custom link */
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ButtonUI, Input, showNotification, useQueryFetch, validationErrorChecker } from 'sonata-react-components';
import { setSonataSession } from 'sonata-static-storage/lib/helpers/session.helpers';

import { useAuth } from '@/context/AuthProvider';
import { useRedirectToMain } from '@/hooks/redirectTo.hook';
import { loginUser } from '@/services/authentication.service';
import { setSession } from '@/store/global/session/selectors';
import { getLoginSchema, type LoginFormValues } from '@/validators/user.validator';

import './styles.scss';

type State = LoginFormValues;

export default function LoginPage() {
  const { formatMessage } = useIntl();
  const loginSchema = getLoginSchema(formatMessage);
  const { refetch: authRefetch } = useAuth();
  const redirectToMain = useRedirectToMain();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({
    email: '',
    password: ''
  });
  const { email, password } = state;

  const { isFetching, refetch } = useQueryFetch(
    [email, password],
    async () => {
      const response = await loginUser(email, password);
      if (response.sessionId) {
        setSession(response);
        setSonataSession(response);
        authRefetch?.();
        const next = new URLSearchParams(window.location.search).get('next');
        if (next && next.startsWith('/') && !next.startsWith('//')) {
          window.location.replace(next);
        } else {
          redirectToMain();
        }
      }
    },
    {
      enabled: false,
      onError: () => {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Invalid credentials',
            id: 'login.errorNotification'
          })
        );
      },
      staleTime: 0
    }
  );

  const errors = validationErrorChecker(state, loginSchema);

  const handleEmailChange = (newEmail: string) => {
    setState(prevState => ({ ...prevState, email: newEmail }));
  };

  const handlePasswordChange = (newPassword: string) => {
    setState(prevState => ({ ...prevState, password: newPassword }));
  };

  const submitLogin = () => {
    if (Object.keys(errors || {}).length > 0) {
      return;
    }
    refetch();
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    submitLogin();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      submitLogin();
    }
  };

  const handleForgotPasswordClick = () => {
    navigate('/forgot-password');
  };

  const handleSignUpClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/sign-up');
  };

  return (
    <div className="login">
      <h1 className="login__title">
        {formatMessage({
          defaultMessage: 'Welcome to ',
          id: 'login.title'
        })}
        <strong>
          {formatMessage({
            defaultMessage: 'Sonata',
            id: 'login.sonata'
          })}
        </strong>
      </h1>

      <form className="login__form" onSubmit={handleLogin}>
        <Input
          id="email"
          inputAdditionalProps={{
            autoComplete: 'email',
            onKeyDown: handleKeyDown
          }}
          label={formatMessage({
            defaultMessage: 'Email',
            id: 'login.emailLabel'
          })}
          name="email"
          onChange={textInput => handleEmailChange(textInput as string)}
          placeholder={formatMessage({
            defaultMessage: 'Enter your email',
            id: 'login.emailPlaceholder'
          })}
          shape="squareUI"
          type="text"
          value={email}
        />
        <Input
          id="password"
          inputAdditionalProps={{
            autoComplete: 'current-password',
            onKeyDown: handleKeyDown
          }}
          label={formatMessage({
            defaultMessage: 'Password',
            id: 'login.passwordLabel'
          })}
          name="password"
          onChange={textInput => handlePasswordChange(textInput as string)}
          placeholder={formatMessage({
            defaultMessage: 'Enter your password',
            id: 'login.passwordPlaceholder'
          })}
          shape="squareUI"
          type="password"
          value={password}
        />
        <div className="login__actions">
          <ButtonUI
            className="login__forgotPasswordLink"
            id="login-forgotPassword-link"
            onClick={handleForgotPasswordClick}
            shape="link"
            text={formatMessage({
              defaultMessage: 'Forgot your password?',
              id: 'login.forgotPassword'
            })}
            type="secondary"
          />
          <ButtonUI
            className="login__button"
            disabled={Object.keys(errors || {}).length > 0}
            id="login-button"
            loading={isFetching}
            onClick={handleLogin}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Enter Sonata',
              id: 'login.loginButton'
            })}
            type="primary"
          />
        </div>
      </form>
      <span className="login__signUpContainer">
        {formatMessage({
          defaultMessage: "Don't have an account?",
          id: 'login.noAccount'
        })}
        <a className="login__signUpLink" href="#" id="login-signUp-link" onClick={handleSignUpClick}>
          {formatMessage({
            defaultMessage: 'Sign up here',
            id: 'login.signUp'
          })}
        </a>
      </span>
    </div>
  );
}
